import React, { useMemo, useRef, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { IFormImperativeHandleProps } from '../../../../../common/form';
import { ExperienceModalProps } from './experience-modal.models';
import { yup } from '../../../../../../services/yup';
import { useStyles } from './experience-modal.styles';
import { Icon, IconTypes, LensesExperienceForm, Modal } from '../../../../../common';
import { LensesExperience } from '../../../../../../constants/profile';
import { v2025PP16783 } from '../../../../../../constants/veeva';

export const ExperienceModal = ({
  onClose,
  onSetValue,
  value,
  ...props
}: ExperienceModalProps): JSX.Element => {
  const ref = useRef<IFormImperativeHandleProps>(null);
  const intl = useIntl();
  const classes = useStyles();
  const [clValue, setClValue] = useState<LensesExperience | null>(value || null);

  const defaultValues = useMemo(() => ({ clExperience: value || ('' as LensesExperience) }), [value]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        clExperience: yup
          .string()
          .required(intl.formatMessage({ id: 'common.errors.required.long' })),
      }),
    [intl],
  );

  const kidsExperienceOptions = useMemo(
    () => [
      {
        label: <FormattedMessage id="createKidsProfile.clExperience.NEWWEAR" />,
        value: LensesExperience.newWear,
      },
      {
        label: <FormattedMessage id="createKidsProfile.clExperience.LESSTHANONEYEAR" />,
        value: LensesExperience.lessThanOneYear,
      },
      {
        label: <FormattedMessage id="createKidsProfile.clExperience.MORETHANONEYEAR" />,
        value: LensesExperience.moreThanOneYear,
      },
      {
        label: <FormattedMessage id="createKidsProfile.clExperience.DROPOUT" />,
        value: LensesExperience.dropOut,
      },
    ],
    [],
  );

  const onSubmit = async (values: { clExperience: LensesExperience }) => {
    if (!values?.clExperience) {
      return;
    }

    onSetValue(values.clExperience);
    onClose!();
  };

  const onChangeValue = (experience: LensesExperience) => {
    setClValue(experience);
  };

  return (
    <Modal
      {...props}
      onClose={onClose}
      veevaNumber={v2025PP16783}
      content={
        <>
          <h2 className={classes.title}>
            {intl.formatMessage({ id: 'createKidsProfile.clExperience.title' })}
          </h2>
          <LensesExperienceForm
            ref={ref}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            defaultValues={defaultValues}
            isAction
            isDisabled={!clValue}
            getValue={onChangeValue}
            options={kidsExperienceOptions}
            customCheckedIcon={<Icon type={IconTypes.radioCheckedKids} />}
            customUncheckedIcon={<Icon type={IconTypes.radioUncheckedKids} />}
            selectedClassName={classes.kidsRadioSelected}
          />
        </>
      }
    />
  );
};
